import {DateTime} from 'luxon';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {RadioButton} from '../../../../../libs/shared/ui/src/components/booleanInputs/radioButton';
import {NumberInputBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/numberInputBox';
import {DateComboBox} from '../../../common/dateTime/dateComboBox';

/*
 * Props.
 */

export interface RecurrenceEndEditorProps {
  initialUntil?: number;
  initialCount?: number;
  onUntilChange: (arg: number | undefined) => void;
  onCountChange: (arg: number | undefined) => void;
}

/*
 * Styles.
 */

const StyledUntilRadioButtonWrapperDiv = styled.div`
  height: 40px;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 25px 100px;
`;

const StyledCountRadioButtonWrapperDiv = styled.div`
  height: 20px;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 25px auto;
`;

const StyledCountTextWrapperDiv = styled.div`
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 30px 50px auto;
  grid-column-gap: 10px;
`;

/*
 * Component.
 */

enum RecurrenceRuleEndsEnum {
  NEVER = 0,
  UNTIL = 1,
  COUNT = 2,
}

const minCount = 1;
const maxCount = 360;
const defaultCount = 10;

export const RecurrenceEndEditor: FC<RecurrenceEndEditorProps> = (props) => {
  const {initialCount, initialUntil, onCountChange, onUntilChange} = props;
  const defaultUntil = DateTime.now().plus({months: 3}).toMillis();

  const [until, setUntil] = useState<number>(initialUntil || defaultUntil);
  const [count, setCount] = useState<number>(initialCount || defaultCount);
  const [recurrenceRuleEndType, setRecurrenceRuleEndType] = useState<RecurrenceRuleEndsEnum>();

  // Set the initial state.
  useInitialEffect(() => {
    if (!initialUntil && !initialCount) {
      setRecurrenceRuleEndType(RecurrenceRuleEndsEnum.NEVER);
    } else if (initialUntil && !initialCount) {
      setRecurrenceRuleEndType(RecurrenceRuleEndsEnum.UNTIL);
    } else {
      setRecurrenceRuleEndType(RecurrenceRuleEndsEnum.COUNT);
    }
  });

  // Update the parent when our state changes.
  useEffect(() => {
    switch (recurrenceRuleEndType) {
      case RecurrenceRuleEndsEnum.NEVER:
        onUntilChange(undefined);
        onCountChange(undefined);
        break;
      case RecurrenceRuleEndsEnum.UNTIL:
        onUntilChange(until);
        onCountChange(undefined);
        break;
      case RecurrenceRuleEndsEnum.COUNT:
        onUntilChange(undefined);
        onCountChange(count);
        break;
      default:
        break;
    }
  }, [recurrenceRuleEndType, until, count, onUntilChange, onCountChange]);

  return (
    <>
      <RadioButton
        onChange={() => setRecurrenceRuleEndType(RecurrenceRuleEndsEnum.NEVER)}
        isChecked={recurrenceRuleEndType === RecurrenceRuleEndsEnum.NEVER}
      >
        <Trans id="custom-recurrence-ends-never">Never</Trans>
      </RadioButton>

      <StyledUntilRadioButtonWrapperDiv>
        <RadioButton
          onChange={() => setRecurrenceRuleEndType(RecurrenceRuleEndsEnum.UNTIL)}
          isChecked={recurrenceRuleEndType === RecurrenceRuleEndsEnum.UNTIL}
        />
        <DateComboBox
          selectedDateTime={DateTime.fromMillis(until)}
          dateFormat={{month: 'short', day: 'numeric', year: 'numeric'}}
          onDateSelect={(dateTime) => {
            setRecurrenceRuleEndType(RecurrenceRuleEndsEnum.UNTIL);
            setUntil(dateTime.toMillis());
          }}
        />
      </StyledUntilRadioButtonWrapperDiv>

      <StyledCountRadioButtonWrapperDiv>
        <RadioButton
          onChange={() => setRecurrenceRuleEndType(RecurrenceRuleEndsEnum.COUNT)}
          isChecked={recurrenceRuleEndType === RecurrenceRuleEndsEnum.COUNT}
        />
        <StyledCountTextWrapperDiv>
          <Trans id="custom-recurrence-ends-after">After</Trans>
          <NumberInputBox
            value={count}
            min={minCount}
            max={maxCount}
            onChange={(newCount) => {
              setRecurrenceRuleEndType(RecurrenceRuleEndsEnum.COUNT);
              setCount(newCount || 0);
            }}
          />
          <Trans id="custom-recurrence-ends-after-occurrences">occurrences</Trans>
        </StyledCountTextWrapperDiv>
      </StyledCountRadioButtonWrapperDiv>
    </>
  );
};
